import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestReset = () => {
  const [username, setUsername] = useState('admin');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/request-reset`, {
        username
      });

      setEmailSent(true);
      toast.success('Email enviat! Revisa la teva safata d\'entrada.');
      
      // Redirect to reset page after 2 seconds
      setTimeout(() => {
        navigate('/admin/reset-password');
      }, 2000);
    } catch (error) {
      console.error('Error sol·licitant restabliment:', error);
      toast.error(error.response?.data?.detail || 'Error enviant l\'email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="request-reset-page" className="min-h-screen flex items-center justify-center px-6 py-12 bg-muted/30">
      <div className="w-full max-w-md">
        <Link 
          to="/admin/login" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Tornar al login
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-card rounded-2xl shadow-sm border border-border/50 p-8"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8" />
            </div>
          </div>

          <h1 className="font-heading text-3xl font-medium text-center text-foreground mb-2">
            Recuperar Contrasenya
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            {emailSent 
              ? 'Email enviat amb èxit! Revisa la teva safata d\'entrada.'
              : 'Introdueix el teu usuari per rebre un codi de restabliment'
            }
          </p>

          {emailSent ? (
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-6">
              <p className="text-sm text-green-800 dark:text-green-200 text-center">
                ✅ S&apos;ha enviat un email a <strong>comandes@estelregals.com</strong> amb un codi de 6 dígits.
              </p>
              <p className="text-xs text-green-700 dark:text-green-300 text-center mt-2">
                El codi és vàlid durant 15 minuts.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="username" className="text-base mb-2">Usuari Administrador</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="request-username"
                  className="h-12 rounded-xl"
                  required
                  disabled
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="request-reset-button"
                className="w-full h-12 text-base font-semibold rounded-xl"
              >
                {loading ? 'Enviant...' : 'Enviar codi de restabliment'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/admin/reset-password" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Ja tinc un codi de restabliment
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RequestReset;
